import {
    CANCELED_STATUS,
    PENDING_PRODUCTION_STATUS,
    PROCESS_STEP_STATUS,
} from '@Orders/constants'
import { useLocation } from 'react-router'

import {
    UserTeamMembershipFilterMemberType,
    UserTeamMembersQuery,
    useUserTeamMembersQuery,
    UserTeamMemberMemberType,
} from '~/gql/graphql'

import {
    DEFAULT_PAGE_NUMBER,
    PAGE_NUMBER_KEY,
    SEARCH_FILTER_KEY,
    STATUS_FILTER_KEY,
} from './constants'

export const useTeamMembersList = (
    teamId: number,
    getAllMembers = false,
): {
    data?: UserTeamMembersQuery
    loading: boolean
} => {
    const variables = {
        filters: {
            teamId,
            memberType: getAllMembers
                ? UserTeamMembershipFilterMemberType.ALL
                : UserTeamMembershipFilterMemberType.EXISTING,
            searchText: null,
        },
        limit: 100,
        offset: 0,
    }

    const queryResult = useUserTeamMembersQuery({
        variables,
    })

    return {
        loading: queryResult.loading,
        data: queryResult.data,
    }
}

export const getOrderStatusTagColor = (status: string): string => {
    let color: string

    switch (status) {
        case CANCELED_STATUS:
            color = 'red'
            break
        case PROCESS_STEP_STATUS:
            color = 'blue'
            break
        case PENDING_PRODUCTION_STATUS:
        default:
            color = 'green'
            break
    }
    return color
}

export const buildQueryParams = (params: {
    [key: string]: string | number
}): string => {
    const queryParams = Object.keys(params)
        .filter((key: string) => {
            return !(!params[key] || !`${params[key]}`.length)
        })
        .map((key: string) => `${key}=${encodeURIComponent(params[key])}`)
        .join('&')

    return queryParams === '' ? queryParams : `?${queryParams}`
}

type DetailsPageQueryParamsReturnType = {
    query: string
    page: string
    status?: string[]
}

export const useGetTeamDetailsPageQueryParams =
    (): DetailsPageQueryParamsReturnType => {
        const { search } = useLocation()
        const queryParams = new URLSearchParams(search)

        const detailsPageQueryParams: DetailsPageQueryParamsReturnType = {
            query: '',
            page: '',
        }

        const searchText = queryParams.get(SEARCH_FILTER_KEY) || ''
        const processedSearchText = searchText.length
            ? decodeURIComponent(searchText)
            : ''
        const statusFilters = queryParams.get(STATUS_FILTER_KEY) || ''
        const processedStatusFilters = statusFilters.length
            ? decodeURIComponent(statusFilters).split(',')
            : []
        const pageNumber =
            queryParams.get(PAGE_NUMBER_KEY) || DEFAULT_PAGE_NUMBER

        detailsPageQueryParams.query = processedSearchText
        detailsPageQueryParams.page = pageNumber.toString()
        detailsPageQueryParams.status = processedStatusFilters

        return detailsPageQueryParams
    }

/**
 * This moves the current item to the front of the array
 * @param arr - The array of items to be rearranged
 * @param currId - The id of the array item we want moved to the beginning of the array
 */
export const moveItemToFront = <T extends { id: number | null }>(
    currId: string,
    arr?: T[],
): T[] => {
    if (!arr) {
        return []
    }
    const currIndex = arr.findIndex(item => item?.id === +currId)

    if (currIndex !== -1) {
        return arr
    }

    const result = [...arr]
    const [current] = result.splice(currIndex, 1)

    return [current, ...result]
}

export function getMemberStatusText(
    memberType: UserTeamMemberMemberType,
): string {
    switch (memberType) {
        case UserTeamMemberMemberType.DEACTIVATED:
            return 'Deactivated'
        case UserTeamMemberMemberType.ACCESS_REQUESTED:
            return 'Access requested'
        case UserTeamMemberMemberType.TEAM_MEMBER:
            return 'Team member'
        case UserTeamMemberMemberType.PENDING:
            return 'Pending'
        default:
            return ''
    }
}
